import { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaTrashAlt } from "react-icons/fa";
import { FaPaypal, FaBitcoin, FaUniversity } from "react-icons/fa";
import { SiPayoneer } from "react-icons/si";
import Sidebar from "../Side/Sidebar";
import AdminSidebar from "../Side/AdminSidebar";
import Topbar from "../Side/Topbar";
import PaymentForm from "../Payments/PaymentForm";
import PaymentApiService from "../../ApiServices/PaymentApiServices/PaymentApiService";
import { useAuth } from "../Auth/AuthContext";

const Payment = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const { authState } = useAuth();
  const admin = authState.roles === "Admin";
  const user = authState.user;

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await PaymentApiService.GetByPublisher(user.id);
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleSetActive = async (id) => {
    await PaymentApiService.SetActive(id); // Call your API
    fetchData(); // Refresh list
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this payment method?")) {
      try {
        await PaymentApiService.Delete(id);
        fetchData(); // Refresh the data after deletion
      } catch (error) {
        console.error("Error deleting payment method:", error);
      }
    }
  };

 return (
  <div className="flex min-h-screen bg-gradient-to-tr from-gray-50 via-white to-gray-100">
    {admin ? <AdminSidebar /> : <Sidebar />}

    <div className="flex-1 flex flex-col">
      <Topbar />

      <main className="p-6 pt-24">
        <div className="max-w-6xl mx-auto space-y-10">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-4xl font-extrabold text-gray-800 tracking-tight">💳 Payment Information</h1>
            <button
              onClick={() => setOpenModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
            >
              + Add Payment Details
            </button>
          </div>

          {/* Payment Cards */}
          <div className="bg-white/60 backdrop-blur-md w-full max-w-6xl p-8 rounded-3xl shadow-2xl border border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-700">💼 Saved Payment Methods</h2>

            {loading ? (
              <p className="text-gray-500 animate-pulse">Loading...</p>
            ) : data && data.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {data.map((method) => (
                  <div
                    key={method.id}
                    className={`rounded-2xl border p-6 shadow-md transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                      method.isActive ? "border-blue-600 bg-blue-50" : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <span className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        {method.method === "BTC" && <FaBitcoin className="text-orange-500" />}
                        {method.method === "PayPal" && <FaPaypal className="text-blue-600" />}
                        {method.method === "Payoneer" && <SiPayoneer className="text-red-600" />}
                        {method.method === "Bank" && <FaUniversity className="text-gray-700" />}
                        {method.method}
                      </span>

                      {method.isActive ? (
                        <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full shadow-sm">
                          Active
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetActive(method.id)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Set Active
                        </button>
                      )}
                    </div>

                    <div className="text-sm text-gray-700 space-y-1">
                      {method.method === "BTC" && <p><strong>Address:</strong> {method.btcAddress}</p>}
                      {method.method === "PayPal" && <p><strong>Email:</strong> {method.paypalEmail}</p>}
                      {method.method === "Payoneer" && <p><strong>Email:</strong> {method.payoneerEmail}</p>}
                      {method.method === "Bank" && (
                        <>
                          <p><strong>Bank:</strong> {method.bankName}</p>
                          <p><strong>Account:</strong> {method.accountName}</p>
                          <p><strong>Number:</strong> {method.accountNumber}</p>
                          <p><strong>SWIFT:</strong> {method.swiftCode}</p>
                        </>
                      )}

                      <div className="pt-3 flex justify-end">
                        <button
                          onClick={() => handleDelete(method.id)}
                          className="text-red-500 hover:text-red-700 text-lg"
                          title="Delete"
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm italic">No payment methods found.</p>
            )}

            <div className="mt-8 text-right">
              <button
                onClick={() => setOpenModal(true)}
                className="text-indigo-600 hover:underline text-sm font-medium"
              >
                + Add or Edit Payment Method
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {openModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-2xl p-8 rounded-3xl shadow-2xl relative animate-fade-in">
            <button
              onClick={() => setOpenModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-600 text-3xl"
            >
              &times;
            </button>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">🔐 Add Payment Details</h2>
            <PaymentForm
              onSuccess={() => {
                setOpenModal(false);
                fetchData();
              }}
            />
          </div>
        </div>
      )}
    </div>
  </div>
);


};

export default Payment;
